import { useId, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Star from "@mui/icons-material/Star";

import { HeldPeriod, serializeHeldPeriods } from "features/trading/heldPeriods";
import {
  Bucket,
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import {
  MAIN_STAR_COLOR,
  sortMembers,
  sortMembersHeldFirst,
} from "./constants";
import { BucketEditModal } from "./BucketEditModal";
import { InlineLoading } from "./InlineLoading";

// Per-chip wording of the editor's held / not-held split. The not-held copy
// says what the editor's disclaimer says (services/bucket_report
// ._sum_pair_bucket_rows leaves such a member out of the bucket's totals),
// phrased for one security rather than as a heading over a list.
const HELD_IN_PERIOD_COPY = "Held in the account during the report period";
const NOT_HELD_IN_PERIOD_COPY =
  "Not held in the account during the report period — disregarded " +
  "during calculations";

// How many member chips show before the rest fold behind "Show more": about
// four rows of chips in the report settings panel, so a 100-security bucket
// does not push the page into a long scroll (task 3780).
export const COLLAPSED_CHIP_LIMIT = 20;

interface AccountBucketSummaryProps {
  accountId: number | null | undefined;
  accountLabel: string;
  number: BucketNumberEnum;
  // The report's periods: the editor's asset picker offers only securities
  // the account held across them, and its member list is split by whether
  // each member was held inside them.
  heldPeriods?: HeldPeriod[];
}

/**
 * Compact read-only view of an account's Bucket 1/2 for the report settings:
 * member chips (main assets starred), "Not created" when absent. With the
 * report's periods, members held in them come first and are highlighted —
 * the same held / not-held split the editor's member list draws. A list
 * longer than COLLAPSED_CHIP_LIMIT folds behind Show more / Show less.
 * Clicking the chips or the pencil opens the shared BucketEditModal to
 * add/remove assets.
 */
export const AccountBucketSummary = ({
  accountId,
  accountLabel,
  number,
  heldPeriods,
}: AccountBucketSummaryProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Unfolded for ONE account: whose list is open, not whether a list is. A
  // different account's bucket therefore starts folded with no reset code.
  const [expandedFor, setExpandedFor] = useState<typeof accountId>(null);
  const isExpanded = expandedFor != null && expandedFor === accountId;
  const chipsId = useId();
  // The periods travel with the query: they are what fills each member's
  // `held_in_period`, which the editor's member list splits on. Serialized
  // into the one wire shape the backend parses (`serializeHeldPeriods`),
  // and omitted entirely when there is no period — which is also what
  // keeps the no-period callers of this list on their own cache entry.
  const heldPeriodsParam = useMemo(
    () => serializeHeldPeriods(heldPeriods),
    [heldPeriods],
  );
  const {
    data: bucketsData,
    currentData,
    isFetching,
  } = useListBucketsQuery(
    {
      account: accountId as number,
      ...(heldPeriodsParam ? { held_periods: heldPeriodsParam } : {}),
    },
    { skip: !accountId },
  );
  // Split the chips held / not held — the editor's own split — only once
  // the answer on screen was fetched for THESE periods. Without periods every
  // member's `held_in_period` is False, which would grey out the whole
  // bucket on a screen that never asked about a period; and while a period
  // change is in flight the chips show the previous answer (below, and RTK's
  // own `data`), whose flags describe the OLD periods. `currentData` is set
  // only for the current query args, so until it lands the chips fall back
  // to the plain alphabetical, uncoloured view rather than state a status
  // for a period nobody asked about.
  const hasPeriods = !!heldPeriodsParam && !!currentData;
  const fetched = bucketsData?.results.find((b) => b.number === number);
  // Hold the last answer while a new one is in flight. The query is keyed on
  // the report's periods, so every date edit is a NEW cache entry with no
  // data yet — without this the member chips would blink to a loading hint
  // on every period change, which they never did when the list was keyed on
  // the account alone. The membership does not depend on the period, so the
  // previous answer's members are the right ones to keep showing; only the
  // per-member `held_in_period` flag is period-specific, which is why the
  // held / not-held split above waits for `currentData`. Reset on an account switch, where
  // the held answer belongs to a different account and is simply wrong.
  const held = useRef<{ accountId: typeof accountId; bucket?: Bucket }>({
    accountId,
  });
  if (held.current.accountId !== accountId) held.current = { accountId };
  if (fetched) held.current.bucket = fetched;
  const bucket = fetched ?? held.current.bucket;
  // On first load / account switch there is nothing to show yet — a loading
  // hint instead of a misleading "Not created".
  const isLoadingBuckets = isFetching && !bucket;

  if (!accountId) {
    return (
      <Typography variant="body2" color="text.disabled">
        Select an account
      </Typography>
    );
  }

  if (isLoadingBuckets) {
    return <InlineLoading />;
  }

  const members = bucket
    ? hasPeriods
      ? sortMembersHeldFirst(bucket.assets)
      : sortMembers(bucket.assets)
    : [];
  const isCollapsible = members.length > COLLAPSED_CHIP_LIMIT;
  const hiddenCount = members.length - COLLAPSED_CHIP_LIMIT;
  const shownMembers =
    isCollapsible && !isExpanded
      ? members.slice(0, COLLAPSED_CHIP_LIMIT)
      : members;

  const editButton = (
    <Tooltip title={`Edit Bucket ${number}`}>
      <IconButton size="small" onClick={() => setIsModalOpen(true)}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <Box
        id={chipsId}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={0.5}
        sx={{ cursor: "pointer" }}
        onClick={() => setIsModalOpen(true)}
      >
        {!bucket ? (
          <Chip size="small" variant="outlined" label="Not created" />
        ) : members.length === 0 ? (
          <Chip size="small" variant="outlined" label="Empty" />
        ) : (
          shownMembers.map((member) => {
            const chip = (
              <Chip
                key={member.id}
                size="small"
                label={member.symbol}
                color={
                  hasPeriods && member.held_in_period ? "primary" : "default"
                }
                icon={
                  member.is_main ? (
                    <Star fontSize="small" htmlColor={MAIN_STAR_COLOR} />
                  ) : undefined
                }
              />
            );
            return hasPeriods ? (
              <Tooltip
                key={member.id}
                title={
                  member.held_in_period
                    ? HELD_IN_PERIOD_COPY
                    : NOT_HELD_IN_PERIOD_COPY
                }
              >
                {chip}
              </Tooltip>
            ) : (
              chip
            );
          })
        )}
        {/* A short list keeps the pencil in the chip flow, as it always was;
            a foldable one moves it beside Show more/less, where it stays
            visible whichever way the list is folded. */}
        {!isCollapsible && editButton}
      </Box>
      {isCollapsible && (
        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
          <Button
            size="small"
            aria-expanded={isExpanded}
            aria-controls={chipsId}
            onClick={() => setExpandedFor(isExpanded ? null : accountId)}
            sx={{ textTransform: "none", px: 0.5, minWidth: 0 }}
          >
            {isExpanded ? "Show less" : `Show more (+${hiddenCount})`}
          </Button>
          {editButton}
        </Box>
      )}
      <BucketEditModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        accountId={accountId}
        accountLabel={accountLabel}
        number={number}
        bucket={bucket}
        heldPeriods={heldPeriods}
        // This summary is the report settings' own entry point into the
        // editor, so the holdings status is shown here and nowhere else.
        showHoldingStatus
      />
    </>
  );
};
